import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Select, Table } from "antd";
import { Link } from "react-router-dom";
import { EmptyState } from "@/shared/components/EmptyState";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useEquipmentList } from "../hooks/useEquipment";
import { useEquipmentStore } from "../store/equipment.store";
import type { Equipment, EquipmentStatus } from "../types";
import { formatDateTime } from "../utils/time";
import { EquipmentFormModal } from "./EquipmentFormModal";

const STATUS_OPTIONS: { value: EquipmentStatus; label: string }[] = [
	{ value: "ACTIVE", label: "Faol" },
	{ value: "MAINTENANCE", label: "Ta'mirda" },
	{ value: "INACTIVE", label: "Faol emas" },
	{ value: "RETIRED", label: "Iste'foda" },
];

interface EquipmentTableProps {
	organizationId?: string;
}

export function EquipmentTable({ organizationId }: EquipmentTableProps) {
	const {
		page: currentPage,
		limit: pageSize,
		setPage,
		getFilter,
		setFilters,
	} = useUrlTableState({ prefix: "eq" });
	const statusFilter = getFilter("status") as EquipmentStatus | undefined;
	const openEditModal = useEquipmentStore((s) => s.openEditModal);
	const editingItem = useEquipmentStore((s) => s.editingItem);

	const { data, isLoading } = useEquipmentList({
		organizationId,
		page: currentPage,
		limit: pageSize,
		status: statusFilter,
	});

	const items = data?.data?.items ?? [];
	const total = data?.data?.pagination?.total ?? 0;

	return (
		<>
			<TableToolbar
				filters={
					<Select
						placeholder="Holat"
						allowClear
						style={{ width: 140 }}
						value={statusFilter}
						onChange={(status) => setFilters({ status: status ?? null })}
						options={STATUS_OPTIONS}
					/>
				}
			/>
			<Table
				loading={isLoading}
				locale={{
					emptyText: <EmptyState description="Texnika topilmadi" />,
				}}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{
						key: "name",
						title: "Nomi",
						dataIndex: "name",
						ellipsis: true,
						render: (name: string, record: Equipment) => (
							<Link to={`/equipment/${record.id}`} className="text-info hover:underline">
								{name}
							</Link>
						),
					},
					{
						key: "type",
						title: "Turi",
						width: 140,
						render: (_, record: Equipment) => record.type?.name ?? record.typeId ?? "—",
					},
					{
						key: "assignedStaff",
						title: "Xodim",
						width: 160,
						ellipsis: true,
						render: (_, record: Equipment) => record.assignedStaff?.fullname ?? "—",
					},
					{
						key: "brigade",
						title: "Brigada",
						width: 140,
						ellipsis: true,
						render: (_, record: Equipment) => record.brigade?.name ?? "—",
					},
					{
						key: "serialNumber",
						title: "Seriya raqami",
						dataIndex: "serialNumber",
						width: 120,
						render: (v: string) => <span className="font-mono">{v}</span>,
					},
					{
						key: "licensePlate",
						title: "Davlat raqami",
						dataIndex: "licensePlate",
						width: 120,
						render: (v: string | null) => (v ? <span className="font-mono">{v}</span> : "—"),
					},
					{
						key: "gpsDevice",
						title: "GPS",
						width: 80,
						align: "center",
						render: (_, record: Equipment) =>
							(record.gpsDevice ?? record.gpsDeviceId) ? "Ha" : "Yo'q",
					},
					{
						key: "lastGpsActivity",
						title: "GPS oxirgi aktivlik",
						width: 160,
						render: (_, record: Equipment) =>
							record.gpsDevice?.lastConnectionAt ? (
								<span className="font-mono">
									{formatDateTime(record.gpsDevice.lastConnectionAt)}
								</span>
							) : (
								"—"
							),
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_, record: Equipment) => {
							const menuItems: MenuProps["items"] = [
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
							];

							return (
								<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
									<Button type="text" icon={<MoreOutlined />} size="small" />
								</Dropdown>
							);
						},
					},
				]}
			/>
			{(organizationId || editingItem) && (
				<EquipmentFormModal organizationId={organizationId ?? editingItem?.organizationId ?? ""} />
			)}
		</>
	);
}
